/// <amd-module name="VSS/Features/Markdown/Plugins/AnchoredHeaders" />
import * as MarkdownIt from "markdown-it";

import { Uri } from "VSS/Platform/Util/Url";

import { IState } from "VSS/Features/Markdown/MarkdownIt";
import { getEncodedHeaderText, getTitleFixedWithSpaceAndCasing } from "VSS/Features/Markdown/Utils/MarkdownItUtils";

/**
 * Adds anchor IDs to header tags.
 * Based on several markdown-it plugins, however this correctly wraps it as dependency for requirejs, and adds anchor prefix.
 * Enables sharing header tags if enableSharingAnchor option is true
 */
export class AnchoredHeaders {
    public static anchorNamePrefix = "user-content-";
    public static shareHeaderAnchorClassName = "shareHeaderAnchor";

    private _mdInstance: MarkdownIt.MarkdownIt;

    constructor(md: MarkdownIt.MarkdownIt, enableSharingAnchor: boolean | undefined = false) {
        this._mdInstance = md;
        md.core.ruler.push("header_anchor", state => this._headerTransformer(state, enableSharingAnchor));
    }

    private _headerTransformer = (state: IState, enableSharingAnchor: boolean): void => {
        // Going to -2 because we are looking ahead
        for (let i = 0; i < state.tokens.length - 2; i++) {
            // Look for headers followed by content in the entire state
            if (state.tokens[i].type === "heading_open" && state.tokens[i + 1].type === "inline") {
                const headingContentToken = state.tokens[i + 1];

                if (headingContentToken.content) {
                    // Apending user-content in front for gfm support and to prevent DOM clobbering
                    const anchorName = AnchoredHeaders.anchorNamePrefix + getEncodedHeaderText(this._mdInstance, headingContentToken.content);
                    state.tokens[i].attrPush(["id", anchorName]);

                    // Enable sharing anchors if options is enabled; append anchor tags as children to header tag; the url of the anchor tags is the one that is sharable
                    if (enableSharingAnchor) {
                        // Generate sharable url for this header from the current url
                        const currentUrl = window.location.href;
                        const uri = new Uri(currentUrl);

                        uri.addQueryParam("anchor", getTitleFixedWithSpaceAndCasing(this._mdInstance, headingContentToken.content), true);

                        // Append anchor tags with generated url as the last children of header tag
                        const anchorOpeningToken = new state.Token("link_open", "a", 1);
                        anchorOpeningToken.attrSet("href", uri.absoluteUri);
                        anchorOpeningToken.attrSet("class", AnchoredHeaders.shareHeaderAnchorClassName);
                        anchorOpeningToken.attrSet("aria-labelledby", anchorName);

                        const anchorClosingToken = new state.Token("link_close", "a", -1);
                        state.tokens[i + 1].children.push(anchorOpeningToken, anchorClosingToken);
                    }

                    // Move past content token and closing token
                    i += 2;
                }
            }
        }
    };
}
